"use client";

import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTrigger,
} from "@/components/atoms/drawer";
import { MOBILE_BREAKPOINT } from "@/constants";
import { useAuth } from "@/hooks/useAuth/useAuth";
import { useIsMobile } from "@/hooks/useIsMobile/useIsMobile";
import {
  LOCALE_NAMES,
  LOCALE_SELECT_PLACEHOLDER,
  Locale,
  getPathname,
  routing,
  usePathname,
} from "@/i18n/routing";
import {
  cn,
  generateLinkFromGeneralLink,
  generateTargetFromLink,
} from "@/lib/utils";
import { NavigationDatasourcesQuery } from "@/types/generated";
import {
  BOTTOM_NAVIGATION,
  BOTTOM_NAVIGATION_ITEM_NAME,
  GO_TO_PASS_ITEM_NAME,
  LOGIN_BUTTON,
  LOGIN_BUTTON_ITEM_NAME,
} from "@/utils";
import { useLocale } from "next-intl";
import Image from "next/image";
import { NavLink } from "../footer/BottomFooter";
import { useCallback, useMemo } from "react";
import { Button } from "@/components/atoms/button";
import { Icon } from "@/components/atoms/icon";
import { Link } from "@/components/atoms/link";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/atoms/accordian";
import { Check, Globe } from "lucide-react";
import { INavLink } from "@/components/organisms/hotel-details/layout/navLinkUtils";

interface TemplateMenuProps {
  navigationDatasources?: Omit<NavigationDatasourcesQuery, "__typename">;
  isShowGotoPass?: boolean | null;
  navLinks?: INavLink[] | null;
}

interface NavMenu {
  itemName?: string;
  title?: string | null;
  url?: string | null;
  icon?: {
    cloudflare_url?: string | null;
    url?: string | null;
    alt_text?: string | null;
    caption?: string | null;
  };
}

export default function TemplateMenu({
  navigationDatasources,
  isShowGotoPass,
  navLinks,
}: Readonly<TemplateMenuProps>) {
  const { userStore: user, login } = useAuth();
  const pathname = usePathname();
  const locale = useLocale();
  const isMobile = useIsMobile(MOBILE_BREAKPOINT);

  const onChangeLanguage = useCallback(
    (nextLocale: Locale) => {
      const url = getPathname({
        href: { pathname, query: {} },
        locale: nextLocale,
      });
      globalThis.location.assign(url);
    },
    [pathname],
  );

  const documentId = isMobile
    ? "65804619fec40b3fa0541ee4"
    : "72ec2890fa3a6dade80b3fcf";

  const { topItems, excludedMenuItems, actions, actionLogin } = useMemo(() => {
    const navSources =
      navigationDatasources?.mystay?.shared_datasource
        ?.navigation_datasources ?? [];

    const sideNavigation = navSources.find((n) => n?.documentId === documentId);

    const selectedMenu = sideNavigation?.display_menu_items ?? [];
    const menuItems = sideNavigation?.menu_items ?? [];

    const mapMenu = (menuItem: any): NavMenu | null => {
      if (!menuItem) return null;
      const link = user
        ? menuItem.button_link_after_loggin || menuItem.menu_link
        : menuItem.menu_link;

      return {
        itemName: menuItem.item_name,
        title: user
          ? menuItem.title_after_loggin || menuItem.menu_title || null
          : menuItem.menu_title || null,
        url: generateLinkFromGeneralLink(link, locale) || "#",
        icon: menuItem.icon ?? {},
      };
    };

    const sortedMenus: NavMenu[] = [
      ...selectedMenu
        .map((s: any) =>
          mapMenu(menuItems.find((m: any) => m?.documentId === s.documentId)),
        )
        .filter(Boolean),
      {
        itemName: "customerTestimonial",
        title: "お客様の声",
        url: `/${locale}/hotelreviewsection`,
        icon: {},
      },
    ];

    const excluded = ["01_Home", "02_Hotel List"];

    const top = sortedMenus.filter(
      (i) => i?.itemName && !excluded.includes(i.itemName),
    );

    const excludedList = sortedMenus.filter((i) =>
      excluded.includes(i?.itemName as string),
    );

    const bottomNavigation = navSources.find(
      (n) =>
        n?.item_name === BOTTOM_NAVIGATION_ITEM_NAME ||
        n?.documentId === BOTTOM_NAVIGATION,
    );

    const acts: NavLink[] =
      bottomNavigation?.menu_items
        ?.map((item) => {
          const link = user
            ? item?.button_link_after_loggin || item?.menu_link
            : item?.menu_link;

          return {
            itemName: item?.item_name,
            documentId: item?.documentId,
            title: user
              ? (item?.title_after_loggin ?? item?.menu_title ?? "")
              : item?.menu_title,
            show: item?.show,
            link: generateLinkFromGeneralLink(link, locale) || "#",
            target: generateTargetFromLink(link),
          };
        })
        .filter((i) => i.title && i.documentId) ?? [];

    const loginAction = acts.find(
      (a) =>
        a.itemName === LOGIN_BUTTON_ITEM_NAME || a.documentId === LOGIN_BUTTON,
    );

    return {
      topItems: top,
      excludedMenuItems: excludedList,
      actions: acts,
      actionLogin: loginAction,
    };
  }, [navigationDatasources, documentId, user, locale]);

  const handleDrawerOpenChange = (open: boolean) => {
    if (!open) document.documentElement.style.scrollBehavior = "auto";
    setTimeout(() => {
      document.documentElement.style.scrollBehavior = "smooth";
    }, 1000);
  };

  const handleNavigate = (url?: string | null) => {
    if (!url) return;
    globalThis.location.href = url.startsWith("https") ? url : url;
  };

  return (
    <Drawer onOpenChange={handleDrawerOpenChange}>
      <div className="flex flex-col items-center justify-center">
        <DrawerTrigger asChild>
          <Button
            className="h-6 p-0 hover:bg-white active:bg-white"
            hierarchy="ghost"
            aria-label="Open Menu"
          >
            <Icon icon="menu" />
          </Button>
        </DrawerTrigger>
        <div className="text-2xs leading-3 text-[var(--fill-base-main)] uppercase">
          Menu
        </div>
      </div>

      <DrawerContent
        onCloseAutoFocus={(e) => e.preventDefault()}
        className="fixed top-0 right-0 h-full w-80 bg-[var(--fill-base-main)]"
      >
        <div className="flex flex-col gap-4 overflow-y-auto px-6 py-4">
          <div
            className={cn(
              "flex items-center py-2 pl-4 justify-end",)}
          >
            <DrawerClose asChild>
              <Button
                hierarchy="outline"
                className="h-9 w-9 rounded-full border-none p-0"
              >
                <Icon icon="close" />
              </Button>
            </DrawerClose>
          </div>

          <nav className="flex flex-col items-start">
            {navLinks && navLinks.length > 0
              && navLinks.map((link, i) => (
                  <DrawerClose asChild key={i}>
                    <Button
                      hierarchy="ghost"
                      className="group w-full justify-start py-4 text-[var(--text-gray-base)] hover:bg-[var(--fill-base-white)] hover:text-[var(--text-base-primary)] active:bg-[var(--fill-base-white)] active:text-[var(--text-base-primary)]"
                    >
                    <Link
                      className="w-full text-left text-[var(--text-gray-base)] group-hover:text-[var(--text-base-primary)] group-active:text-[var(--text-base-primary)] group-active:bg-[var(--fill-base-white)]"
                      href={link.url || "#"}
                      onClick={e => {
                        const closeBtn = document.querySelector('[data-radix-dialog-close]');
                        if (closeBtn) (closeBtn as HTMLElement).click();
                      }}
                    >
                      {link.title}
                    </Link>
                    </Button>
                  </DrawerClose>
                ))
            }

           {navLinks && navLinks.length>0 && <div className="my-6 h-px w-full bg-[var(--fill-base-white)] px-4 opacity-60"></div>}
            {excludedMenuItems.map((menu, i) => {
              const customTitle =
                menu.itemName === "01_Home"
                  ? "アイコニア・ホスピタリティ トップ"
                  : menu.itemName === "02_Hotel List"
                    ? "ホテル一覧"
                    : menu.title;

              return (
                <DrawerClose asChild key={`excluded-${i}`}>
                  <Button
                    hierarchy="ghost"
                    className="w-full justify-start py-4 text-[var(--text-gray-base)] hover:bg-[var(--fill-base-white)] hover:text-[var(--text-base-primary)] active:bg-[var(--fill-base-white)] active:text-[var(--text-base-primary)]"
                    onClick={() => handleNavigate(menu.url)}
                  >
                    {customTitle}
                  </Button>
                </DrawerClose>
              );
            })}
            {!!isShowGotoPass &&
              topItems
                .filter((i) => i.itemName === GO_TO_PASS_ITEM_NAME)
                .map((menu, i) => (
                  <DrawerClose asChild key={`goto-${i}`}>
                    <button
                      onClick={() => handleNavigate(menu.url)}
                      className="mt-4 flex h-[50px] w-full items-center justify-center gap-1 bg-[var(--button-go-to-pass-bg)] px-1 py-4 text-white"
                    >
                      <p className="text-sm">{menu.title}</p>
                      {(menu.icon?.cloudflare_url || menu.icon?.url) && (
                        <div className="relative h-5 min-w-5">
                          <Image
                            src={
                              menu.icon.cloudflare_url || menu.icon.url || ""
                            }
                            alt={menu.icon.alt_text || ""}
                            aria-label={menu.icon.caption || ""}
                            fill
                          />
                        </div>
                      )}
                    </button>
                  </DrawerClose>
                ))}
          </nav>

          <div className="mt-4 flex flex-col gap-1.5">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="language" className="border-none">
                <AccordionTrigger className="my-3 w-full min-w-[184.75px] justify-between rounded-lg px-4 py-2 font-[700] text-[var(--text-gray-base)] hover:bg-[var(--fill-base-white)] hover:text-[var(--text-base-primary)]">
                  <div className="flex items-center gap-2">
                    <Globe className="h-5 w-5" />
                    <span data-wovn-ignore>{LOCALE_SELECT_PLACEHOLDER}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  {routing.locales.map((lang) => (
                    <div
                      key={lang}
                      data-wovn-ignore
                      aria-current={locale === lang ? "true" : undefined}
                      onClick={() => onChangeLanguage(lang)}
                      className={cn(
                        "my-2 flex w-full cursor-pointer items-center justify-between rounded-lg px-2 py-1 text-[var(--text-gray-lighter)] hover:bg-[var(--fill-base-white)] hover:text-[var(--text-base-primary)]",
                        locale === lang &&
                          "bg-[var(--fill-base-white)] text-[var(--text-base-primary)]",
                      )}
                    >
                      <span>{LOCALE_NAMES[lang]}</span>
                      {locale === lang && <Check className="h-4 w-4 shrink-0" />}
                    </div>
                  ))}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
